import React, { useCallback, useEffect, useRef, useState } from "react";
import { Volume2, Play } from "lucide-react";
import { SHARED_ORIGIN } from "@/lib/api";
import { playChime } from "@/features/staffTill/helpers";

/** Iter 660 — spoken payment announcements (till + vendor dashboard).
 *  Polls a paid-feed every 4 s; the FIRST poll only seeds the seen-set
 *  (nothing spoken on load). Each later NEW session (oldest first):
 *  chime → POST /announce/tts → play the cached mp3 by URL.
 *  On/off is per device (localStorage `storageKey`); language is per
 *  store (feed `lang`, owner sets it with the EN/हिन्दी chips passed in
 *  via `langControl`). Chrome autoplay: a short silent clip is played on
 *  the Switch tap (user gesture) to unlock the shared <audio> element. */

// ~50 ms of silence — ONLY used to unlock the audio element on the
// toggle gesture; every announcement plays a plain https mp3.
const SILENT_WAV =
  "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA==";

const LANG_NAME = { en: "English", hi: "हिन्दी" };

export default function PaymentAnnouncer({
  prefix, // "till-announce" | "owner-announce"
  storageKey, // "announce:till" | "announce:owner"
  fetchFeed, // async () => {lang, paid:[{session_id, amount, name, paid_at}]}
  requestTts, // async (session_id, lang) => {url, text}
  langControl = null, // optional node (owner EN/हिन्दी chips)
}) {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(storageKey) === "1");
  const [lang, setLang] = useState("en");
  const [last, setLast] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const seenRef = useRef(null); // null = not seeded yet
  const audioRef = useRef(null);
  const feedRef = useRef([]);

  const getAudio = () => {
    if (!audioRef.current) audioRef.current = new Audio();
    return audioRef.current;
  };

  const speak = useCallback(
    async (p, currentLang) => {
      try {
        playChime("payment");
        const { url, text } = await requestTts(p.session_id, currentLang);
        const audio = getAudio();
        audio.src = `${SHARED_ORIGIN || ""}${url}`;
        await audio.play();
        setLast(text || "");
        setError("");
      } catch (err) {
        const d = err?.response?.data?.detail;
        setError(typeof d === "string" ? d : "Couldn't play the announcement.");
      }
    },
    [requestTts],
  );

  useEffect(() => {
    if (!enabled) {
      seenRef.current = null;
      return undefined;
    }
    let stopped = false;
    const tick = async () => {
      try {
        const data = await fetchFeed();
        if (stopped) return;
        const paid = data?.paid || [];
        feedRef.current = paid;
        if (data?.lang) setLang(data.lang);
        if (seenRef.current === null) {
          // First poll after enabling: seed only — never announce on load.
          seenRef.current = new Set(paid.map((p) => p.session_id));
          return;
        }
        const fresh = paid.filter((p) => !seenRef.current.has(p.session_id)).reverse();
        for (const p of fresh) {
          seenRef.current.add(p.session_id);
          // eslint-disable-next-line no-await-in-loop
          await speak(p, data?.lang || "en");
        }
      } catch {
        if (!stopped) setError("Couldn't reach the payment feed — retrying…");
      }
    };
    tick();
    const t = setInterval(tick, 4000);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, [enabled, fetchFeed, speak]);

  const toggle = () => {
    const on = !enabled;
    setEnabled(on);
    localStorage.setItem(storageKey, on ? "1" : "0");
    setError("");
    if (on) {
      // User gesture — unlock the audio element for later programmatic plays.
      try {
        const audio = getAudio();
        audio.src = SILENT_WAV;
        audio.play().catch(() => {});
      } catch { /* ignore */ }
    }
  };

  const testVoice = async () => {
    setBusy(true);
    try {
      let paid = feedRef.current;
      if (!paid.length) {
        const data = await fetchFeed();
        paid = data?.paid || [];
        feedRef.current = paid;
        if (data?.lang) setLang(data.lang);
      }
      if (!paid.length) {
        setError("No paid bills yet — the test voice needs at least one paid session.");
        return;
      }
      await speak(paid[0], lang);
    } catch {
      setError("Couldn't reach the payment feed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`${prefix}-card`}
      className="rounded-2xl border border-amber-200 bg-amber-50 p-4"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2 font-bold text-slate-900 text-sm">
          <Volume2 className="w-4 h-4 text-amber-600" /> Announce payments on this device
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={toggle}
          data-testid={`${prefix}-toggle`}
          className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
            enabled ? "bg-[color:var(--brand-orange)]" : "bg-slate-300"
          }`}
        >
          <span
            className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-[left] ${
              enabled ? "left-[22px]" : "left-0.5"
            }`}
          />
        </button>
      </div>
      <p className="mt-1.5 text-xs text-slate-600">
        Speaks &ldquo;Payment received, $45.00 from Priya&rdquo; when a bill is paid ·{" "}
        <span className="font-bold">{LANG_NAME[lang] || lang}</span>
      </p>
      {langControl}
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={testVoice}
          disabled={busy}
          data-testid={`${prefix}-test`}
          className="inline-flex items-center gap-1.5 rounded-full bg-white border border-amber-300 text-amber-800 text-xs font-bold px-3 py-1.5 hover:bg-amber-100 disabled:opacity-60 transition-colors"
        >
          <Play className="w-3 h-3" /> Test voice
        </button>
        {last && (
          <span data-testid={`${prefix}-last`} className="text-xs text-slate-500 truncate">
            Last: &ldquo;{last}&rdquo;
          </span>
        )}
      </div>
      {error && (
        <div data-testid={`${prefix}-error`} className="mt-2 text-xs font-semibold text-red-600">
          {error}
        </div>
      )}
    </div>
  );
}
